import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'
import produce from 'immer'
import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { CheckboxChangeEvent } from 'primereact/checkbox'
import { Dialog } from 'primereact/dialog'
import { Divider } from 'primereact/divider'
import { InputNumber, InputNumberChangeEvent } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { Tooltip } from 'primereact/tooltip'
import { classNames } from 'primereact/utils'

import { DateTemplate } from '../../../../../../components/DateTemplate'
import { InputNumberToggleable } from '../../../../../../components/inputs'
import { StatusTag } from '../../../../../../components/tableTemplates'
import { useToastContext } from '../../../../../../contexts'
import {
  OrderResponse,
  OrderResponseItem,
  PatchRequest,
  WebEdiDto,
} from '../../../../../../data/models'
import { getHandleableError } from '../../../../../../utils/errors/isXiaError'
import { calculateBreakpointWidth } from '../../../../../../utils/helper/calculateBreakpointWidth'
import { isOrderModified } from '../../../../../../utils/helper/checkConfirmationCodes'
import { delay } from '../../../../../../utils/helper/delay'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../../utils/helper/validateFormikFields'
import { calculateItemPrice } from '../../../../../../utils/price-calculation'
import { ConfigurableComponent } from '../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../ui-config'
import {
  requestPatchOrderById,
  requestPutOrderItemById,
  useOrderItem,
  useOrderItems,
} from '../../../../data'
import { getConfirmationCode } from '../../../../utils'

interface Props {
  visible: boolean
  setVisible: (params: boolean) => void
  id: string
  itemId: string
  setDisplayRejectOrderItem: (params: boolean) => void
  setSelectedItems: any
  currency: string
}

export interface FormValues {
  confirmationItemId: OrderResponseItem['ConfirmationItemId']
  confirmationCountryOfOrigin: OrderResponseItem['ConfirmationCountryOfOrigin']
  confirmationSellerProductId: OrderResponseItem['ConfirmationSellerProductId']
  confirmationSellerProductDescription: OrderResponseItem['ConfirmationSellerProductDescription']
  confirmationQuantity?: OrderResponseItem['ConfirmationQuantity']
  confirmationUnit?: OrderResponseItem['ConfirmationUnit']
  confirmationPriceBaseQuantity?: OrderResponseItem['ConfirmationPriceBaseQuantity']
  confirmationPriceUnitOfMeasure?: OrderResponseItem['ConfirmationPriceUnitOfMeasure']
  confirmationPrice?: OrderResponseItem['ConfirmationPrice']
  confirmationPriceSum?: OrderResponseItem['ConfirmationPriceSum']
  confirmationPriceSumChecked: boolean
  confirmationAllowanceChargeSum?: OrderResponseItem['ConfirmationAllowanceChargeSum']
  confirmationLineItemAmount?: OrderResponseItem['ConfirmationLineItemAmount']
  confirmationLineItemAmountChecked: boolean
  confirmationDeliveryDate?: Date
}

type FormFieldName = keyof FormValues

export const Edit: React.FC<Props> = ({
  visible,
  setVisible,
  id,
  itemId,
  setDisplayRejectOrderItem,
  setSelectedItems,
  currency,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n
  const { uiConfig: config } = useConfigContext()

  const { data, isLoading } = useOrderItem(id, itemId)
  const { data: dataItems } = useOrderItems(id)

  const [confirmationCode, setConfirmationCode] =
    useState<OrderResponseItem['ConfirmationCode']>(undefined)

  useEffect(() => {
    if (data) {
      setConfirmationCode(data.ConfirmationCode)
    }
  }, [data])

  const { showToast } = useToastContext()
  const [isRequestRunning, setIsRequestRunning] = useState(false)
  // const [isCalculating, setIsCalculating] = useState(false)
  const [isCalculatingPriceSum, setIsCalculatingPriceSum] = useState(false)
  const [isCalculatingLineItemAmount, setIsCalculatingLineItemAmount] =
    useState(false)

  const calculateConfirmationPriceSum = async (
    confirmationQuantity: number,
    confirmationBaseQuantity: number,
    confirmationPrice: number,
    force: boolean = false,
  ) => {
    if (!formik.values.confirmationPriceSumChecked || force) {
      setIsCalculatingPriceSum(true)

      const priceSum = calculateItemPrice(
        confirmationQuantity,
        confirmationBaseQuantity,
        confirmationPrice,
      )

      await formik.setFieldValue('confirmationPriceSum', priceSum)
      await delay(1)

      setIsCalculatingPriceSum(false)

      return priceSum
    }
  }

  const calculateConfirmationLineItemAmount = async (
    priceSum: number,
    allowanceChargeSum: number,
    force: boolean = false,
  ) => {
    if (!formik.values.confirmationLineItemAmountChecked || force) {
      setIsCalculatingLineItemAmount(true)

      await formik.setFieldValue(
        'confirmationLineItemAmount',
        priceSum + allowanceChargeSum,
      )
      await delay(1)

      setIsCalculatingLineItemAmount(false)
    }
  }

  const onConfirmationQuantityChange = async (
    event: InputNumberChangeEvent,
  ) => {
    const value = event?.value || 1
    const confirmationQuantity = value
    const confirmationPrice = (formik.values.confirmationPrice as number) || 0
    const confirmationBaseQuantity =
      (formik.values.confirmationPriceBaseQuantity as number) || 0
    const confirmationAllowanceChargeSum =
      (formik.values.confirmationAllowanceChargeSum as number) || 0

    await formik.setFieldValue('confirmationQuantity', confirmationQuantity)

    const priceSum =
      ((await calculateConfirmationPriceSum(
        confirmationQuantity,
        confirmationBaseQuantity,
        confirmationPrice,
      )) as number) || 0

    await calculateConfirmationLineItemAmount(
      priceSum,
      confirmationAllowanceChargeSum,
    )
  }

  const onConfirmationPriceChange = async (e: InputNumberChangeEvent) => {
    const confirmationPrice = (e.value as number) || 0
    const confirmationQuantity =
      (formik.values.confirmationQuantity as number) || 0
    const confirmationPriceBaseQuantity =
      (formik.values.confirmationPriceBaseQuantity as number) || 0
    const confirmationAllowanceChargeSum =
      (formik.values.confirmationAllowanceChargeSum as number) || 0

    await formik.setFieldValue('confirmationPrice', confirmationPrice)

    const priceSum =
      ((await calculateConfirmationPriceSum(
        confirmationQuantity,
        confirmationPriceBaseQuantity,
        confirmationPrice,
      )) as number) || 0

    await calculateConfirmationLineItemAmount(
      priceSum,
      confirmationAllowanceChargeSum,
    )
  }

  const onConfirmationAllowanceChargeSumChange = async (
    e: InputNumberChangeEvent,
  ) => {
    const allowanceChargeSum = (e.value as number) || 0
    const priceSum = (formik.values.confirmationPriceSum as number) || 0

    await formik.setFieldValue(
      'confirmationAllowanceChargeSum',
      allowanceChargeSum,
    )

    await calculateConfirmationLineItemAmount(priceSum, allowanceChargeSum)
  }

  const onConfirmationPriceSumChange = async (e: InputNumberChangeEvent) => {
    const priceSum = (e.value as number) || 0
    const allowanceChargeSum =
      (formik.values.confirmationAllowanceChargeSum as number) || 0
    await formik.setFieldValue('confirmationPriceSum', priceSum)
    await calculateConfirmationLineItemAmount(priceSum, allowanceChargeSum)
  }

  const onConfirmationPriceSumCheckedChange = async (
    e: CheckboxChangeEvent,
  ) => {
    await formik.setFieldValue('confirmationPriceSumChecked', e.checked)

    if (!e.checked) {
      const price = (formik.values.confirmationPrice as number) || 0
      const quantity = (formik.values.confirmationQuantity as number) || 0
      const baseQuantity =
        (formik.values.confirmationPriceBaseQuantity as number) || 0
      const allowanceChargeSum =
        (formik.values.confirmationAllowanceChargeSum as number) || 0

      const priceSum =
        ((await calculateConfirmationPriceSum(
          quantity,
          baseQuantity,
          price,
          true,
        )) as number) || 0

      await calculateConfirmationLineItemAmount(priceSum, allowanceChargeSum)
    }
  }

  const onConfirmationLineItemAmountCheckedChange = async (
    e: CheckboxChangeEvent,
  ) => {
    await formik.setFieldValue('confirmationLineItemAmountChecked', e.checked)

    if (!e.checked) {
      const allowanceChargeSum =
        (formik.values.confirmationAllowanceChargeSum as number) || 0
      const priceSum = (formik.values.confirmationPriceSum as number) || 0

      await calculateConfirmationLineItemAmount(
        priceSum,
        allowanceChargeSum,
        true,
      )
    }
  }

  // ************************************************************
  // formik
  // ************************************************************
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      confirmationItemId: data?.ConfirmationItemId || '',
      confirmationCountryOfOrigin: data?.ConfirmationCountryOfOrigin || '',
      confirmationSellerProductId: data?.ConfirmationSellerProductId || '',
      confirmationSellerProductDescription:
        data?.ConfirmationSellerProductDescription || '',
      confirmationQuantity: data?.ConfirmationQuantity || 0,
      confirmationUnit: data?.ConfirmationUnit || '',
      confirmationPriceBaseQuantity: data?.ConfirmationPriceBaseQuantity || 0,
      confirmationPriceUnitOfMeasure:
        data?.ConfirmationPriceUnitOfMeasure || '',
      confirmationPrice: data?.ConfirmationPrice || 0,
      confirmationPriceSum: data?.ConfirmationPriceSum || 0,
      confirmationPriceSumChecked:
        data?.Meta?.Fields?.DisabledCalculation?.ConfirmationPriceSum || false,
      confirmationAllowanceChargeSum: data?.ConfirmationAllowanceChargeSum || 0,
      confirmationLineItemAmount: data?.ConfirmationLineItemAmount || 0,
      confirmationLineItemAmountChecked:
        data?.Meta?.Fields?.DisabledCalculation?.ConfirmationLineItemAmount ||
        false,
      confirmationDeliveryDate: data?.ConfirmationDeliveryDate
        ? new Date(data?.ConfirmationDeliveryDate)
        : new Date(),
    },
    validate: formData => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          // confirmationItemId
          fieldName: 'confirmationItemId',
          config: config.orderResponse.editOrderItem.confirmationItemId,
        },
        {
          // confirmationCountryOfOrigin
          fieldName: 'confirmationCountryOfOrigin',
          config:
            config.orderResponse.editOrderItem.confirmationCountryOfOrigin,
        },
        {
          // confirmationSellerProductId
          fieldName: 'confirmationSellerProductId',
          config:
            config.orderResponse.editOrderItem.confirmationSellerProductId,
        },
        {
          // confirmationSellerProductDescription
          fieldName: 'confirmationSellerProductDescription',
          config:
            config.orderResponse.editOrderItem
              .confirmationSellerProductDescription,
        },
        {
          // confirmationQuantity
          fieldName: 'confirmationQuantity',
          config: config.orderResponse.editOrderItem.confirmationQuantity,
        },
        {
          // confirmationUnit
          fieldName: 'confirmationUnit',
          config: config.orderResponse.editOrderItem.confirmationUnit,
        },
        {
          // confirmationPriceBaseQuantity
          fieldName: 'confirmationPriceBaseQuantity',
          config:
            config.orderResponse.editOrderItem.confirmationPriceBaseQuantity,
        },
        {
          // confirmationPriceUnitOfMeasure
          fieldName: 'confirmationPriceUnitOfMeasure',
          config:
            config.orderResponse.editOrderItem.confirmationPriceUnitOfMeasure,
        },
        {
          // confirmationPrice
          fieldName: 'confirmationPrice',
          config: config.orderResponse.editOrderItem.confirmationPrice,
        },
        {
          // confirmationPriceSum
          fieldName: 'confirmationPriceSum',
          config: config.orderResponse.editOrderItem.confirmationPriceSum,
        },
        {
          // confirmationAllowanceChargeSum
          fieldName: 'confirmationAllowanceChargeSum',
          config:
            config.orderResponse.editOrderItem.confirmationAllowanceChargeSum,
        },
        {
          // confirmationLineItemAmount
          fieldName: 'confirmationLineItemAmount',
          config: config.orderResponse.editOrderItem.confirmationLineItemAmount,
        },
        {
          // confirmationDeliveryDate
          fieldName: 'confirmationDeliveryDate',
          config: config.orderResponse.editOrderItem.confirmationDeliveryDate,
        },
      ]

      validateFormikFields(fieldsToValidate, formData, errors)

      if (
        formData.confirmationDeliveryDate &&
        data?.MinConfirmationDeliveryDate
      ) {
        const confirmationDeliveryDate = formData.confirmationDeliveryDate
        const minDeliveryDate = new Date(data?.MinConfirmationDeliveryDate)

        if (confirmationDeliveryDate < minDeliveryDate) {
          errors.confirmationDeliveryDate = t(
            'orderResponse.positions.table.edit.dialog.form.confirmationDeliveryDate.validation.minDeliveryDate',
            { date: minDeliveryDate.toLocaleDateString() },
          )
        }
      }

      if (
        formData.confirmationDeliveryDate &&
        data?.MaxConfirmationDeliveryDate
      ) {
        const confirmationDeliveryDate = formData.confirmationDeliveryDate
        const maxDeliveryDate = new Date(data?.MaxConfirmationDeliveryDate)

        if (confirmationDeliveryDate > maxDeliveryDate) {
          errors.confirmationDeliveryDate = t(
            'orderResponse.positions.table.edit.dialog.form.confirmationDeliveryDate.validation.maxDeliveryDate',
            { date: maxDeliveryDate.toLocaleDateString() },
          )
        }
      }

      return errors
    },
    onSubmit: async formData => {
      // TODO: onsubmit überall gleich aufbauen
      // deactivate the buttons
      setIsRequestRunning(true)

      try {
        // close the dialog and reset
        handleClose()

        // submit form
        await submitForm(formData)

        // mutate()
        // globalMutate(`/OrderResponses/${id}/Items`)
        // show success toast
        showToast({
          style: {},
          closable: true,
          severity: 'success',
          summary: t(
            'orderResponse.positions.table.edit.dialog.save.messages.success.summary',
          ),
          detail: t(
            'orderResponse.positions.table.edit.dialog.save.messages.success.detail',
            {
              orderPosition: data?.OrderPosition,
            },
          ),
        })
      } catch (error) {
        let handleableError = getHandleableError(error)
        let errorMessage = handleableError.message

        // show error toast
        showToast({
          style: {},
          closable: true,
          severity: 'error',
          sticky: true,
          summary: t(
            'orderResponse.positions.table.edit.dialog.save.messages.failure.summary',
          ),
          detail: errorMessage,
        })
      } finally {
        setSelectedItems([]) // deselect the items in the table
        setIsRequestRunning(false)
      }
    },
  })

  const isFormFieldValid = (name: FormFieldName) =>
    !(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    return (
      !isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    )
  }

  // submit form
  const submitForm = async (formData: FormValues) => {
    const newData: OrderResponseItem = produce(data!, draft => {
      draft.ConfirmationItemId = formData.confirmationItemId
      draft.ConfirmationCountryOfOrigin = formData.confirmationCountryOfOrigin
      draft.ConfirmationSellerProductId = formData.confirmationSellerProductId
      draft.ConfirmationSellerProductDescription =
        formData.confirmationSellerProductDescription
      draft.ConfirmationQuantity = formData.confirmationQuantity as number
      draft.ConfirmationUnit = formData.confirmationUnit as string
      draft.ConfirmationPriceBaseQuantity =
        formData.confirmationPriceBaseQuantity as number
      draft.ConfirmationPriceUnitOfMeasure =
        formData.confirmationPriceUnitOfMeasure
      draft.ConfirmationPrice = formData.confirmationPrice as number
      draft.ConfirmationPriceSum = formData.confirmationPriceSum as number
      draft.Meta!.Fields!.DisabledCalculation!.ConfirmationPriceSum =
        formData.confirmationPriceSumChecked || false
      draft.ConfirmationAllowanceChargeSum =
        formData.confirmationAllowanceChargeSum
      draft.ConfirmationLineItemAmount = formData.confirmationLineItemAmount
      draft.Meta!.Fields!.DisabledCalculation!.ConfirmationLineItemAmount =
        formData.confirmationLineItemAmountChecked || false
      draft.ConfirmationDeliveryDate =
        formData.confirmationDeliveryDate?.toISOString()
      draft.ConfirmationCode = getConfirmationCode(
        draft,
        data,
        confirmationCode,
      )

      // Remove rejection if there was any
      if (draft?.ConfirmationReason) {
        draft.ConfirmationReason.Code = undefined
        draft.ConfirmationReason.Description = undefined
      }
    })

    // patch for ConfirmationCode
    const newDataArray: OrderResponseItem[] =
      dataItems?.map(item => (item.Id === newData.Id ? newData : item)) || []

    const notSetCode: OrderResponse['ConfirmationCode'] = 'NOT_SET'
    const modifiedCode: OrderResponse['ConfirmationCode'] = 'MODIFIED'
    const patchData: PatchRequest<WebEdiDto<OrderResponse>> = [
      {
        op: 'replace',
        path: '/Payload/ConfirmationCode',
        value: isOrderModified(newDataArray) ? modifiedCode : notSetCode,
      },
    ]

    await requestPutOrderItemById(id, itemId, newData)
    await requestPatchOrderById(id, patchData)
  }

  // handleReset
  const handleReset = async () => {
    await formik.setFieldValue('confirmationItemId', data?.ItemId)
    await formik.setFieldValue(
      'confirmationCountryOfOrigin',
      data?.CountryOfOrigin,
    )
    await formik.setFieldValue(
      'confirmationSellerProductId',
      data?.SellerProductId,
    )
    await formik.setFieldValue(
      'confirmationSellerProductDescription',
      data?.SellerProductDescription,
    )
    await formik.setFieldValue('confirmationQuantity', data?.OrderQuantity)
    await formik.setFieldValue('confirmationUnit', data?.OrderUnit)
    await formik.setFieldValue(
      'confirmationPriceBaseQuantity',
      data?.OrderPriceBaseQuantity,
    )
    await formik.setFieldValue(
      'confirmationPriceUnitOfMeasure',
      data?.OrderPriceUnitOfMeasure,
    )
    await formik.setFieldValue('confirmationPrice', data?.OrderPrice)
    await formik.setFieldValue('confirmationPriceSum', data?.OrderPriceSum)
    await formik.setFieldValue('confirmationPriceSumChecked', false)
    await formik.setFieldValue(
      'confirmationAllowanceChargeSum',
      data?.AllowanceChargeSum,
    )
    await formik.setFieldValue(
      'confirmationLineItemAmount',
      data?.LineItemAmount,
    )
    await formik.setFieldValue('confirmationLineItemAmountChecked', false)
    await formik.setFieldValue(
      'confirmationDeliveryDate',
      data?.OrderDeliveryDate ? new Date(data.OrderDeliveryDate) : undefined,
    )

    setConfirmationCode('NOT_SET')
  }

  // handleClose
  const handleClose = () => {
    formik.resetForm()
    setConfirmationCode(data?.ConfirmationCode)
    setVisible(false)
  }

  // ************************************************************
  // components
  // ************************************************************
  // itemId
  const itemIdLabel = <label htmlFor="itemId" className="text-sm"></label>

  const itemIdComponent = (
    <InputText id="itemId" className="w-full" value={data?.ItemId} disabled />
  )

  const itemIdWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={itemIdComponent}
      label={itemIdLabel}
      isLoading={isLoading}
    />
  )

  // confirmationItemId
  const confirmationItemIdLabel = (
    <label
      htmlFor="confirmationItemId"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationItemId'),
      })}
    ></label>
  )

  const confirmationItemIdComponent = (
    <InputText
      id="confirmationItemId"
      value={formik.values.confirmationItemId}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationItemId'),
      })}
      disabled={
        getConfirmationCode(formik.values, data, confirmationCode) ===
        'REJECTED'
      }
    />
  )

  const confirmationItemIdErrorMsg = (
    <div>{getFormErrorMessage('confirmationItemId')}</div>
  )

  const confirmationItemIdWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={confirmationItemIdComponent}
      label={confirmationItemIdLabel}
      errorMsg={confirmationItemIdErrorMsg}
      isLoading={isLoading}
    />
  )

  // countryOfOrigin
  const countryOfOriginLabel = (
    <label htmlFor="countryOfOrigin" className="text-sm"></label>
  )

  const countryOfOriginComponent = (
    <InputText
      id="countryOfOrigin"
      className="w-full"
      value={data?.CountryOfOrigin}
      disabled
    />
  )

  const countryOfOriginWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={countryOfOriginComponent}
      label={countryOfOriginLabel}
      isLoading={isLoading}
    />
  )

  // confirmationCountryOfOrigin
  const confirmationCountryOfOriginLabel = (
    <label
      htmlFor="confirmationCountryOfOrigin"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationCountryOfOrigin'),
      })}
    ></label>
  )

  const confirmationCountryOfOriginComponent = (
    <InputText
      id="confirmationCountryOfOrigin"
      value={formik.values.confirmationCountryOfOrigin}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationCountryOfOrigin'),
      })}
      disabled={
        getConfirmationCode(formik.values, data, confirmationCode) ===
        'REJECTED'
      }
    />
  )

  const confirmationCountryOfOriginErrorMsg = (
    <div>{getFormErrorMessage('confirmationCountryOfOrigin')}</div>
  )

  const confirmationCountryOfOriginWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={confirmationCountryOfOriginComponent}
      label={confirmationCountryOfOriginLabel}
      errorMsg={confirmationCountryOfOriginErrorMsg}
      isLoading={isLoading}
    />
  )

  // gtin
  const gtinLabel = <label htmlFor="gtin" className="text-sm"></label>

  const gtinComponent = (
    <InputText id="gtin" className="w-full" value={data?.GTIN} disabled />
  )

  const gtinWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={gtinComponent}
      label={gtinLabel}
      isLoading={isLoading}
    />
  )

  // buyerProductId
  const buyerProductIdLabel = (
    <label htmlFor="buyerProductId" className="text-sm"></label>
  )

  const buyerProductIdComponent = (
    <InputText
      id="buyerProductId"
      className="w-full"
      value={data?.BuyerProductId}
      disabled
    />
  )

  const buyerProductIdWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={buyerProductIdComponent}
      label={buyerProductIdLabel}
      isLoading={isLoading}
    />
  )

  // confirmationSellerProductId
  const confirmationSellerProductIdLabel = (
    <label
      htmlFor="confirmationSellerProductId"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationSellerProductId'),
      })}
    ></label>
  )

  const confirmationSellerProductIdComponent = (
    <InputText
      id="confirmationSellerProductId"
      value={formik.values.confirmationSellerProductId}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationSellerProductId'),
      })}
      disabled={
        getConfirmationCode(formik.values, data, confirmationCode) ===
        'REJECTED'
      }
    />
  )

  const confirmationSellerProductIdErrorMsg = (
    <div>{getFormErrorMessage('confirmationSellerProductId')}</div>
  )

  const confirmationSellerProductIdWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={confirmationSellerProductIdComponent}
      label={confirmationSellerProductIdLabel}
      errorMsg={confirmationSellerProductIdErrorMsg}
      isLoading={isLoading}
    />
  )

  // buyerProductDescription
  const buyerProductDescriptionLabel = (
    <label htmlFor="buyerProductDescription" className="text-sm"></label>
  )

  const buyerProductDescriptionComponent = (
    <InputText
      id="buyerProductDescription"
      className="w-full"
      value={data?.BuyerProductDescription}
      disabled
    />
  )

  const buyerProductDescriptionWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={buyerProductDescriptionComponent}
      label={buyerProductDescriptionLabel}
      isLoading={isLoading}
    />
  )

  // confirmationSellerProductDescription
  const confirmationSellerProductDescriptionLabel = (
    <label
      htmlFor="confirmationSellerProductDescription"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationSellerProductDescription'),
      })}
    ></label>
  )

  const confirmationSellerProductDescriptionComponent = (
    <InputText
      id="confirmationSellerProductDescription"
      value={formik.values.confirmationSellerProductDescription}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationSellerProductDescription'),
      })}
      disabled={
        getConfirmationCode(formik.values, data, confirmationCode) ===
        'REJECTED'
      }
    />
  )

  const confirmationSellerProductDescriptionErrorMsg = (
    <div>{getFormErrorMessage('confirmationSellerProductDescription')}</div>
  )

  const confirmationSellerProductDescriptionWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={confirmationSellerProductDescriptionComponent}
      label={confirmationSellerProductDescriptionLabel}
      errorMsg={confirmationSellerProductDescriptionErrorMsg}
      isLoading={isLoading}
    />
  )

  //  orderQuantity
  const orderQuantityLabel = (
    <label htmlFor="orderQuantity" className="text-sm"></label>
  )

  const orderQuantityComponent = (
    <InputNumber
      id="orderQuantity"
      className="w-full"
      value={data?.OrderQuantity}
      locale={language}
      disabled
    />
  )

  const orderQuantityWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={orderQuantityComponent}
      label={orderQuantityLabel}
      isLoading={isLoading}
    />
  )

  // confirmationQuantity
  const confirmationQuantityLabel = (
    <label
      htmlFor="confirmationQuantity"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationQuantity'),
      })}
    ></label>
  )

  const confirmationQuantityComponent = (
    <InputNumber
      id="confirmationQuantity"
      value={formik.values.confirmationQuantity}
      onChange={onConfirmationQuantityChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationQuantity'),
      })}
      allowEmpty={false}
      min={1}
      locale={language}
      disabled={
        getConfirmationCode(formik.values, data, confirmationCode) ===
        'REJECTED'
      }
    />
  )

  const confirmationQuantityErrorMsg = (
    <div>{getFormErrorMessage('confirmationQuantity')}</div>
  )

  const confirmationQuantityWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={confirmationQuantityComponent}
      label={confirmationQuantityLabel}
      errorMsg={confirmationQuantityErrorMsg}
      isLoading={isLoading}
    />
  )

  // orderUnit
  const orderUnitLabel = <label htmlFor="orderUnit" className="text-sm"></label>

  const orderUnitComponent = (
    <InputText
      id="orderUnit"
      className="w-full"
      value={data?.OrderUnit}
      disabled
    />
  )

  const orderUnitWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={orderUnitComponent}
      label={orderUnitLabel}
      isLoading={isLoading}
    />
  )

  //  confirmationUnit
  const confirmationUnitLabel = (
    <label
      htmlFor="confirmationUnit"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationUnit'),
      })}
    ></label>
  )

  const confirmationUnitComponent = (
    <InputText
      id="confirmationUnit"
      value={formik.values.confirmationUnit}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationUnit'),
      })}
      disabled={
        getConfirmationCode(formik.values, data, confirmationCode) ===
        'REJECTED'
      }
    />
  )

  const confirmationUnitErrorMsg = (
    <div>{getFormErrorMessage('confirmationUnit')}</div>
  )

  const confirmationUnitWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={confirmationUnitComponent}
      label={confirmationUnitLabel}
      errorMsg={confirmationUnitErrorMsg}
      isLoading={isLoading}
    />
  )

  // orderPriceBaseQuantity
  const orderPriceBaseQuantityLabel = (
    <label htmlFor="orderPriceBaseQuantity" className="text-sm"></label>
  )

  const orderPriceBaseQuantityComponent = (
    <InputNumber
      id="orderPriceBaseQuantity"
      className="w-full"
      value={data?.OrderPriceBaseQuantity}
      locale={language}
      disabled
    />
  )

  const orderPriceBaseQuantityWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={orderPriceBaseQuantityComponent}
      label={orderPriceBaseQuantityLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPriceBaseQuantity
  const confirmationPriceBaseQuantityLabel = (
    <label
      htmlFor="confirmationPriceBaseQuantity"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationPriceBaseQuantity'),
      })}
    ></label>
  )

  const confirmationPriceBaseQuantityComponent = (
    <InputNumber
      id="confirmationPriceBaseQuantity"
      value={formik.values.confirmationPriceBaseQuantity}
      onChange={(e: any) =>
        formik.setFieldValue('confirmationPriceBaseQuantity', e.value)
      }
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationPriceBaseQuantity'),
      })}
      locale={language}
      disabled={
        getConfirmationCode(formik.values, data, confirmationCode) ===
        'REJECTED'
      }
    />
  )

  const confirmationPriceBaseQuantityErrorMsg = (
    <div>{getFormErrorMessage('confirmationPriceBaseQuantity')}</div>
  )

  const confirmationPriceBaseQuantityWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={confirmationPriceBaseQuantityComponent}
      label={confirmationPriceBaseQuantityLabel}
      errorMsg={confirmationPriceBaseQuantityErrorMsg}
      isLoading={isLoading}
    />
  )

  // orderPriceUnitOfMeasure
  const orderPriceUnitOfMeasureLabel = (
    <label htmlFor="orderPriceUnitOfMeasure" className="text-sm"></label>
  )

  const orderPriceUnitOfMeasureComponent = (
    <InputText
      id="orderPriceUnitOfMeasure"
      className="w-full"
      value={data?.OrderPriceUnitOfMeasure}
      disabled
    />
  )

  const orderPriceUnitOfMeasureWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={orderPriceUnitOfMeasureComponent}
      label={orderPriceUnitOfMeasureLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPriceUnitOfMeasure
  const confirmationPriceUnitOfMeasureLabel = (
    <label
      htmlFor="confirmationPriceUnitOfMeasure"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationPriceUnitOfMeasure'),
      })}
    ></label>
  )

  const confirmationPriceUnitOfMeasureComponent = (
    <InputText
      id="confirmationPriceUnitOfMeasure"
      value={formik.values.confirmationPriceUnitOfMeasure?.toUpperCase()}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationPriceUnitOfMeasure'),
      })}
      disabled={
        getConfirmationCode(formik.values, data, confirmationCode) ===
        'REJECTED'
      }
    />
  )

  const confirmationPriceUnitOfMeasureErrorMsg = (
    <div>{getFormErrorMessage('confirmationPriceUnitOfMeasure')}</div>
  )

  const confirmationPriceUnitOfMeasureWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={confirmationPriceUnitOfMeasureComponent}
      label={confirmationPriceUnitOfMeasureLabel}
      errorMsg={confirmationPriceUnitOfMeasureErrorMsg}
      isLoading={isLoading}
    />
  )

  // orderPrice
  const orderPriceLabel = (
    <label htmlFor="orderPrice" className="text-sm"></label>
  )
  const orderPriceComponent = (
    <InputNumber
      id="orderPrice"
      value={data?.OrderPrice}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const orderPriceWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={orderPriceComponent}
      label={orderPriceLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPrice
  const confirmationPriceLabel = (
    <label
      htmlFor="confirmationPrice"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationPrice'),
      })}
    ></label>
  )

  const confirmationPriceComponent = (
    <InputNumber
      id="confirmationPrice"
      value={formik.values.confirmationPrice}
      onChange={(e: any) => onConfirmationPriceChange(e)}
      mode="currency"
      currency={currency}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationPrice'),
      })}
      allowEmpty={false}
      locale={language}
      disabled={
        getConfirmationCode(formik.values, data, confirmationCode) ===
        'REJECTED'
      }
    />
  )

  const confirmationPriceErrorMsg = (
    <div>{getFormErrorMessage('confirmationPrice')}</div>
  )

  const confirmationPriceWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={confirmationPriceComponent}
      label={confirmationPriceLabel}
      errorMsg={confirmationPriceErrorMsg}
      isLoading={isLoading}
    />
  )

  // orderPriceSum
  const orderPriceSumLabel = (
    <label htmlFor="orderPriceSum" className="text-sm"></label>
  )

  const orderPriceSumComponent = (
    <InputNumber
      id="orderPriceSum"
      value={data?.OrderPriceSum}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const orderPriceSumWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={orderPriceSumComponent}
      label={orderPriceSumLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPriceSum
  const confirmationPriceSumLabel = (
    <label
      htmlFor="confirmationPriceSum"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationPriceSum'),
      })}
    >
      <Tooltip target="#custom-target-icon" />
      <i
        id="custom-target-icon"
        className=" pi pi-info-circle  ml-1 text-xs"
        data-pr-tooltip={t('common.tooltips.calculating')}
        data-pr-position="right"
        data-pr-at="right+5 center"
      />
    </label>
  )

  const confirmationPriceSumComponent = (
    <InputNumberToggleable
      id="confirmationPriceSum"
      checked={formik.values.confirmationPriceSumChecked}
      onCheckedChange={onConfirmationPriceSumCheckedChange}
      mode="currency"
      currency={currency}
      value={formik.values.confirmationPriceSum}
      onChange={onConfirmationPriceSumChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationPriceSum'),
      })}
      allowEmpty={false}
      locale={language}
      disabled={
        getConfirmationCode(formik.values, data, confirmationCode) ===
        'REJECTED'
      }
    />
  )

  const confirmationPriceSumErrorMsg = (
    <div>{getFormErrorMessage('confirmationPriceSum')}</div>
  )

  const confirmationPriceSumWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={confirmationPriceSumComponent}
      label={confirmationPriceSumLabel}
      errorMsg={confirmationPriceSumErrorMsg}
      isCalculating={isCalculatingPriceSum}
      isLoading={isLoading}
    />
  )

  // allowanceChargeSum
  const allowanceChargeSumLabel = (
    <label htmlFor="allowanceChargeSum" className="text-sm"></label>
  )

  const allowanceChargeSumComponent = (
    <InputNumber
      id="allowanceChargeSum"
      value={data?.AllowanceChargeSum}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const allowanceChargeSumWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={allowanceChargeSumComponent}
      label={allowanceChargeSumLabel}
      isLoading={isLoading}
    />
  )

  // confirmationAllowanceChargeSum
  const confirmationAllowanceChargeSumLabel = (
    <label
      htmlFor="confirmationAllowanceChargeSum"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationAllowanceChargeSum'),
      })}
    ></label>
  )

  const confirmationAllowanceChargeSumComponent = (
    <InputNumber
      id="confirmationAllowanceChargeSum"
      mode="currency"
      currency={currency}
      value={formik.values.confirmationAllowanceChargeSum}
      onChange={onConfirmationAllowanceChargeSumChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationAllowanceChargeSum'),
      })}
      locale={language}
      disabled={
        getConfirmationCode(formik.values, data, confirmationCode) ===
        'REJECTED'
      }
    />
  )

  const confirmationAllowanceChargeSumErrorMsg = (
    <div>{getFormErrorMessage('confirmationAllowanceChargeSum')}</div>
  )

  const confirmationAllowanceChargeSumWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={confirmationAllowanceChargeSumComponent}
      label={confirmationAllowanceChargeSumLabel}
      errorMsg={confirmationAllowanceChargeSumErrorMsg}
      isLoading={isLoading}
    />
  )

  // lineItemAmount
  const lineItemAmountLabel = (
    <label htmlFor="lineItemAmount" className="text-sm"></label>
  )

  const lineItemAmountComponent = (
    <InputNumber
      id="lineItemAmount"
      value={data?.LineItemAmount}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const lineItemAmountWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={lineItemAmountComponent}
      label={lineItemAmountLabel}
      isLoading={isLoading}
    />
  )

  // confirmationLineItemAmount
  const confirmationLineItemAmountLabel = (
    <label
      htmlFor="confirmationLineItemAmount"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationLineItemAmount'),
      })}
    ></label>
  )

  const confirmationLineItemAmountComponent = (
    <InputNumberToggleable
      id="confirmationLineItemAmount"
      checked={formik.values.confirmationLineItemAmountChecked}
      onCheckedChange={onConfirmationLineItemAmountCheckedChange}
      mode="currency"
      currency={currency}
      value={formik.values.confirmationLineItemAmount}
      onChange={(e: any) =>
        formik.setFieldValue('confirmationLineItemAmount', e.value)
      }
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationLineItemAmount'),
      })}
      locale={language}
      disabled={
        getConfirmationCode(formik.values, data, confirmationCode) ===
        'REJECTED'
      }
    />
  )

  const confirmationLineItemAmountErrorMsg = (
    <div>{getFormErrorMessage('confirmationLineItemAmount')}</div>
  )

  const confirmationLineItemAmountWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={confirmationLineItemAmountComponent}
      label={confirmationLineItemAmountLabel}
      errorMsg={confirmationLineItemAmountErrorMsg}
      isLoading={isLoading}
      isCalculating={isCalculatingLineItemAmount}
    />
  )

  // orderDeliveryDate
  const orderDeliveryDateLabel = (
    <label htmlFor="orderDeliveryDate" className="text-sm"></label>
  )

  const orderDeliveryDate = data?.OrderDeliveryDate
    ? new Date(data?.OrderDeliveryDate)
    : null

  const orderDeliveryDateComponent = (
    <Calendar
      id="orderDeliveryDate"
      value={orderDeliveryDate}
      className="w-full"
      showIcon
      locale={language}
      disabled
    />
  )

  const orderDeliveryDateWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={orderDeliveryDateComponent}
      label={orderDeliveryDateLabel}
      isLoading={isLoading}
    />
  )

  // confirmationDeliveryDate
  const confirmationDeliveryDateLabel = (
    <label
      htmlFor="confirmationDeliveryDate"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationDeliveryDate'),
      })}
    ></label>
  )

  const minDate = data?.MinConfirmationDeliveryDate
    ? new Date(data?.MinConfirmationDeliveryDate)
    : undefined
  const maxDate = data?.MaxConfirmationDeliveryDate
    ? new Date(data?.MaxConfirmationDeliveryDate)
    : undefined

  const confirmationDeliveryDateComponent = (
    <Calendar
      id="confirmationDeliveryDate"
      value={formik.values.confirmationDeliveryDate}
      onChange={e => {
        const newDate = e
        const confirmationDeliveryDate = formik.values.confirmationDeliveryDate
        if (newDate && confirmationDeliveryDate) {
          newDate.value?.setHours(
            confirmationDeliveryDate.getHours(),
            confirmationDeliveryDate.getMinutes(),
            confirmationDeliveryDate.getSeconds(),
          )
        }

        formik.handleChange(newDate)
      }}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationDeliveryDate'),
      })}
      showButtonBar
      showIcon
      minDate={minDate}
      maxDate={maxDate}
      dateTemplate={(date: any) => (
        <DateTemplate
          date={date}
          desiredDeliveryDate={data?.OrderDeliveryDate}
        />
      )}
      locale={language}
      disabled={
        getConfirmationCode(formik.values, data, confirmationCode) ===
        'REJECTED'
      }
    />
  )

  const confirmationDeliveryDateErrorMsg = (
    <div>{getFormErrorMessage('confirmationDeliveryDate')}</div>
  )

  const confirmationDeliveryDateWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={confirmationDeliveryDateComponent}
      label={confirmationDeliveryDateLabel}
      errorMsg={confirmationDeliveryDateErrorMsg}
      isLoading={isLoading}
    />
  )

  // ************************************************************
  // dialog components
  // ************************************************************
  // header
  const header = (
    <>
      <div className="flex align-content-center flex-wrap">
        <span className="mr-3">
          {t('orderResponse.positions.table.edit.dialog.title', {
            orderPosition: data?.OrderPosition,
          })}
        </span>

        <StatusTag
          status={getConfirmationCode(formik.values, data, confirmationCode)}
        />
      </div>
    </>
  )

  // footer
  const isModified = formik.dirty
  const isReset =
    data?.ConfirmationCode === 'REJECTED' && confirmationCode === 'NOT_SET'
  const isDisabled = !(isModified || isReset)

  const footer = (
    <div className="flex flex-wrap justify-content-between pt-3">
      <Button
        className="p-button-text w-full md:w-auto"
        label={t('orderResponse.positions.table.edit.dialog.reset.label')}
        icon="pi pi-replay"
        onClick={handleReset}
        type="reset"
        disabled={
          !(
            getConfirmationCode(formik.values, data, confirmationCode) ===
              'MODIFIED' ||
            getConfirmationCode(formik.values, data, confirmationCode) ===
              'REJECTED'
          )
        }
      />

      <Button
        className="p-button-text ml-auto w-full md:w-auto mb-1 md:mb-0"
        label={t('orderResponse.positions.table.edit.dialog.cancel.label')}
        icon="pi pi-times"
        onClick={handleClose}
        type="button"
      />

      <Button
        label={t('orderResponse.positions.table.edit.dialog.save.label')}
        className="w-full md:w-auto"
        icon={'pi pi-check'}
        type="submit"
        onClick={formik.submitForm}
        disabled={isDisabled}
        loading={isRequestRunning}
      />
    </div>
  )

  return (
    <Dialog
      header={header}
      visible={visible}
      style={{ width: '55rem' }}
      breakpoints={{ [calculateBreakpointWidth(55)]: '90vw' }}
      footer={footer}
      onHide={handleClose}
      blockScroll
    >
      {/* itemId */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{itemIdWrapper}</div>
        <div className="col-12 md:col-6">{confirmationItemIdWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* countryOfOrigin */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{countryOfOriginWrapper}</div>
        <div className="col-12 md:col-6">
          {confirmationCountryOfOriginWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* gtin */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{gtinWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* buyerProductId */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{buyerProductIdWrapper}</div>
        <div className="col-12 md:col-6">
          {confirmationSellerProductIdWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* buyerProductDescription */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{buyerProductDescriptionWrapper}</div>
        <div className="col-12 md:col-6">
          {confirmationSellerProductDescriptionWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* quantity */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{orderQuantityWrapper}</div>

        <div className="col-12 md:col-6">{confirmationQuantityWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* unit */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{orderUnitWrapper}</div>

        <div className="col-12 md:col-6">{confirmationUnitWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* priceBaseQuantity */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{orderPriceBaseQuantityWrapper}</div>

        <div className="col-12 md:col-6">
          {confirmationPriceBaseQuantityWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* priceUnitOfMeasure */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{orderPriceUnitOfMeasureWrapper}</div>

        <div className="col-12 md:col-6">
          {confirmationPriceUnitOfMeasureWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* price */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{orderPriceWrapper}</div>

        <div className="col-12 md:col-6">{confirmationPriceWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* confirmationPriceSum */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{orderPriceSumWrapper}</div>

        <div className="col-12 md:col-6">{confirmationPriceSumWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* allowanceChargeSum */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{allowanceChargeSumWrapper}</div>

        <div className="col-12 md:col-6">
          {confirmationAllowanceChargeSumWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* ItemAmount */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{lineItemAmountWrapper}</div>

        <div className="col-12 md:col-6">
          {confirmationLineItemAmountWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* deliveryDate */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{orderDeliveryDateWrapper}</div>

        <div className="col-12 md:col-6">{confirmationDeliveryDateWrapper}</div>
      </div>
    </Dialog>
  )
}
